/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DuctedCombinationsData,
  DuctedCompatibilityState,
  DuctedRepresentativeData,
  DuctedResultSet
} from './ducted-compatibility-types';
import {
  beginRepresentatives,
  failRepresentatives,
  finishRepresentatives,
  representativeEntriesFor
} from './ducted-representative-entries';

export type {
  AppliedRegulation,
  DuctedCombinationsData,
  DuctedCompatibilityEntry,
  DuctedCompatibilityState,
  DuctedRepresentativeData,
  DuctedRepresentativeEntries,
  DuctedRepresentativeEntry,
  DuctedResultSet
} from './ducted-compatibility-types';

export const DUCTED_RESULT_SETS: readonly DuctedResultSet[] = [
  'kept',
  'alternatives'
];

export const defaultDuctedCompatibilityState: DuctedCompatibilityState = {
  resultsBySystem: {},
  representativesBySystem: {}
};

const emptyData: DuctedCombinationsData = {
  combinations: [],
  totalCount: 0,
  systemTypes: [],
  filterOptions: [],
  modelSpecs: {},
  productData: {},
  accessoryData: {},
  sizingApplied: false,
  sizingNearestFit: false,
  sizingRelaxed: false,
  regulationsApplied: false,
  appliedRegulations: []
};

export const ductedCompatibilitySlice = createSlice({
  name: 'ductedCompatibility',
  initialState: defaultDuctedCompatibilityState,
  reducers: {
    beginDuctedCombinationsFetch: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        resultSet: DuctedResultSet;
        requestBodyHash: string;
        // Tail page (pageNumber > 1): the loaded rows are being extended,
        // not replaced.
        append?: boolean;
        // The system types the page-one request covers: their Recommended
        // rows ride the same response, so they go loading under the same
        // hash here. A tail page sends none (the cards stay put).
        representativeTypeCodes?: string[];
      }>
    ) => {
      const {
        systemUniqueId,
        resultSet,
        requestBodyHash,
        append,
        representativeTypeCodes
      } = action.payload;
      if (!state.resultsBySystem[systemUniqueId]) {
        state.resultsBySystem[systemUniqueId] = {};
      }
      const existing = state.resultsBySystem[systemUniqueId][resultSet];
      // Previous data is kept under the loading state (a failed refresh
      // falls back to it); the new hash becomes the only one finish/fail
      // may apply. A page-one fetch over an existing entry is a refresh:
      // the rail shows skeletons in place of the rows about to be replaced.
      state.resultsBySystem[systemUniqueId][resultSet] = existing
        ? {
            ...existing,
            status: 'loading',
            requestBodyHash,
            refreshing: append !== true
          }
        : { ...emptyData, status: 'loading', requestBodyHash };
      if (representativeTypeCodes && representativeTypeCodes.length > 0) {
        beginRepresentatives(
          representativeEntriesFor(state, systemUniqueId, resultSet),
          representativeTypeCodes,
          requestBodyHash
        );
      }
    },
    finishDuctedCombinationsFetch: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        resultSet: DuctedResultSet;
        requestBodyHash: string;
        data: DuctedCombinationsData;
        // Tail page (pageNumber > 1, AHA-6681 CS-2): concatenate onto the
        // already-loaded combinations instead of replacing them.
        append?: boolean;
        // The Recommended row per system type the page-one response carried.
        // A type that went loading on begin but is absent here lands as the
        // honest empty row (null combination).
        representativesByType?: Record<string, DuctedRepresentativeData>;
      }>
    ) => {
      const {
        systemUniqueId,
        resultSet,
        requestBodyHash,
        data,
        append,
        representativesByType
      } = action.payload;
      const existing = state.resultsBySystem[systemUniqueId]?.[resultSet];
      // Stale response (a newer fetch began since): drop it.
      if (!existing || existing.requestBodyHash !== requestBodyHash) return;
      if (!append) {
        state.resultsBySystem[systemUniqueId][resultSet] = {
          ...data,
          status: 'loaded',
          requestBodyHash,
          refreshing: false
        };
        finishRepresentatives(
          representativeEntriesFor(state, systemUniqueId, resultSet),
          representativesByType ?? {},
          requestBodyHash
        );
        return;
      }
      // Append semantics: concat deduped by ID (a shifting universe can
      // repeat rows across pages), shallow-merge the enrichment maps, take
      // the latest totalCount/facets.
      const seenIds = new Set(existing.combinations.map((combo) => combo.ID));
      state.resultsBySystem[systemUniqueId][resultSet] = {
        ...data,
        combinations: [
          ...existing.combinations,
          ...data.combinations.filter((combo) => !seenIds.has(combo.ID))
        ],
        modelSpecs: { ...existing.modelSpecs, ...data.modelSpecs },
        productData: { ...existing.productData, ...data.productData },
        accessoryData: { ...existing.accessoryData, ...data.accessoryData },
        status: 'loaded',
        requestBodyHash,
        refreshing: false
      };
    },
    failDuctedCombinationsFetch: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        resultSet: DuctedResultSet;
        requestBodyHash: string;
      }>
    ) => {
      const { systemUniqueId, resultSet, requestBodyHash } = action.payload;
      const existing = state.resultsBySystem[systemUniqueId]?.[resultSet];
      if (!existing || existing.requestBodyHash !== requestBodyHash) return;
      // Previous data preserved — a failed refresh keeps the last results,
      // and the rail shows them again in place of the skeletons. The
      // Recommended rows that rode this request keep their last row too.
      state.resultsBySystem[systemUniqueId][resultSet] = {
        ...existing,
        refreshing: false,
        status: 'error'
      };
      failRepresentatives(
        state.representativesBySystem[systemUniqueId]?.[resultSet] ?? {},
        requestBodyHash
      );
    },
    // Honest zero for a set that never fetched (wants gateway, bounded to
    // nothing, or the unanchored-without-codes guard): no POST was made.
    recordDuctedCombinationsSkipped: (
      state,
      action: PayloadAction<{
        systemUniqueId: string;
        resultSet: DuctedResultSet;
      }>
    ) => {
      const { systemUniqueId, resultSet } = action.payload;
      if (!state.resultsBySystem[systemUniqueId]) {
        state.resultsBySystem[systemUniqueId] = {};
      }
      state.resultsBySystem[systemUniqueId][resultSet] = {
        ...emptyData,
        status: 'skipped',
        requestBodyHash: null
      };
    },
    clearDuctedCombinations: (state) => {
      state.resultsBySystem = {};
      state.representativesBySystem = {};
    }
  }
});

export const {
  beginDuctedCombinationsFetch,
  finishDuctedCombinationsFetch,
  failDuctedCombinationsFetch,
  recordDuctedCombinationsSkipped,
  clearDuctedCombinations
} = ductedCompatibilitySlice.actions;

export default ductedCompatibilitySlice.reducer;
